import { RefObject, useEffect } from 'react';

import { useTheme } from '@mui/material';

import {
  CALENDAR_SCROLLBAR_TRACK_PADDING,
  CALENDAR_SCROLL_INSET_CSS_VAR,
  CALENDAR_SCROLL_INSET_WITHOUT_OVERFLOW,
  CALENDAR_SCROLL_INSET_WITH_OVERFLOW,
} from '@/shared/lib/calendarLayout.constants';

export interface VerticalScrollbarOptions {
  enabled?: boolean;
  alwaysVisible?: boolean;
  alwaysSolid?: boolean;
  hideTrack?: boolean;
  compactLayout?: boolean;
  thumbColor?: string;
  trackColor?: string;
  getScroller?: (container: HTMLElement) => HTMLElement | null;
  getContentElement?: (scroller: HTMLElement) => HTMLElement | null;
  getTrackParent?: (scroller: HTMLElement, container: HTMLElement) => HTMLElement | null;
  getTrackBounds?: (
    scroller: HTMLElement,
    container: HTMLElement,
  ) => { top: number; height: number };
  key?: string | number;
  trackTransform?: string;
  thumbWidth?: number;
  trackWidth?: number;
}

const useVerticalScrollbar = (
  containerRef: RefObject<HTMLElement | null>,
  options: VerticalScrollbarOptions = {},
) => {
  const theme = useTheme();

  useEffect(() => {
    if (options.enabled === false) return;

    const container = containerRef.current;
    if (!container) return;

    const thumbColor = options.thumbColor ?? theme.palette.scrollbar.thumb;
    const trackColor = options.trackColor ?? theme.palette.scrollbar.track;

    let teardown: (() => void) | undefined;

    const init = () => {
      const scroller = options.getScroller?.(container) ?? container;
      if (!scroller) return false;

      const trackParent = options.getTrackParent?.(scroller, container) ?? scroller;
      if (!trackParent) return false;

      const originalPosition = getComputedStyle(trackParent).position;
      if (originalPosition === 'static') {
        trackParent.style.position = 'relative';
      }

      const thumbWidth = options.thumbWidth ?? 6;
      const trackWidth = options.trackWidth ?? 6;
      const clipWidth = options.compactLayout
        ? Math.max(thumbWidth, trackWidth)
        : Math.max(thumbWidth, trackWidth) + 6;

      const clipWrapper = document.createElement('div');
      Object.assign(clipWrapper.style, {
        position: 'absolute',
        right: '0',
        width: `${clipWidth}px`,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: '10',
        ...(options.trackTransform ? { transform: options.trackTransform } : {}),
      });

      const alwaysSolid = options.alwaysSolid ?? true;

      const track = document.createElement('div');
      Object.assign(track.style, {
        position: 'absolute',
        right: options.compactLayout ? '0' : '1px',
        width: `${trackWidth}px`,
        backgroundColor: options.hideTrack ? 'transparent' : trackColor,
        borderRadius: options.hideTrack ? '0' : '28px',
        boxShadow: options.hideTrack ? 'none' : 'inset 1px 1px 1.5px 0 rgba(0, 0, 0, 0.15)',
        opacity: '0',
        transition: alwaysSolid ? 'none' : 'opacity 0.3s ease',
        pointerEvents: 'auto',
      });

      const thumb = document.createElement('div');
      Object.assign(thumb.style, {
        position: 'absolute',
        left: options.compactLayout ? '0' : '-1px',
        width: `${thumbWidth}px`,
        backgroundColor: thumbColor,
        borderRadius: '20px',
        cursor: 'grab',
        opacity: '0',
        transition: alwaysSolid ? 'none' : 'opacity 0.3s ease',
      });

      track.appendChild(thumb);
      clipWrapper.appendChild(track);
      trackParent.appendChild(clipWrapper);

      let hideTimeout: ReturnType<typeof setTimeout>;
      let isDragging = false;
      let startY = 0;
      let startScrollTop = 0;

      const getTrackBounds =
        options.getTrackBounds ??
        ((s: HTMLElement) => ({
          top: s.scrollTop,
          height: s.clientHeight,
        }));

      const setScrollableInset = (scrollable: boolean) => {
        scroller.style.setProperty(
          CALENDAR_SCROLL_INSET_CSS_VAR,
          scrollable ? CALENDAR_SCROLL_INSET_WITH_OVERFLOW : CALENDAR_SCROLL_INSET_WITHOUT_OVERFLOW,
        );
      };

      let boundsHeight = 0;

      const syncPosition = () => {
        const { top, height } = getTrackBounds(scroller, container);
        boundsHeight = height;
        clipWrapper.style.top = `${top}px`;
        clipWrapper.style.height = `${height}px`;
        track.style.top = `${CALENDAR_SCROLLBAR_TRACK_PADDING}px`;
        track.style.height = `${height - CALENDAR_SCROLLBAR_TRACK_PADDING * 2}px`;
      };

      const applyThumbPosition = () => {
        const trackHeight = Math.max(0, boundsHeight - CALENDAR_SCROLLBAR_TRACK_PADDING * 2);
        const scrollable = scroller.scrollHeight > scroller.clientHeight;
        const visibleRatio =
          scroller.clientHeight / Math.max(scroller.scrollHeight, scroller.clientHeight);
        const thumbHeight = Math.max(40, visibleRatio * trackHeight);
        const maxThumbTop = trackHeight - thumbHeight;
        const thumbTop = scrollable
          ? (scroller.scrollTop / (scroller.scrollHeight - scroller.clientHeight)) * maxThumbTop
          : 0;
        thumb.style.height = `${thumbHeight}px`;
        thumb.style.top = `${thumbTop}px`;
      };

      const updateThumb = () => {
        syncPosition();

        const scrollable = scroller.scrollHeight > scroller.clientHeight;
        setScrollableInset(scrollable);

        if (!scrollable && !options.alwaysVisible) {
          track.style.opacity = '0';
          return;
        }

        applyThumbPosition();

        track.style.opacity = '1';
        thumb.style.opacity = '1';
        if (!alwaysSolid) {
          clearTimeout(hideTimeout);
          hideTimeout = setTimeout(() => {
            if (!isDragging) {
              track.style.opacity = '0.35';
              thumb.style.opacity = '0.4';
            }
          }, 1000);
        }
      };

      const onThumbMouseDown = (e: MouseEvent) => {
        e.preventDefault();
        isDragging = true;
        startY = e.clientY;
        startScrollTop = scroller.scrollTop;
        thumb.style.cursor = 'grabbing';
        track.style.opacity = '1';
        thumb.style.opacity = '1';
        if (!alwaysSolid) clearTimeout(hideTimeout);
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      };

      const onMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        const dy = e.clientY - startY;
        const thumbHeight = parseFloat(thumb.style.height);
        const trackH = Math.max(0, boundsHeight - CALENDAR_SCROLLBAR_TRACK_PADDING * 2);
        const maxThumbTop = trackH - thumbHeight;
        const maxScroll = scroller.scrollHeight - scroller.clientHeight;
        scroller.scrollTop = startScrollTop + (dy / maxThumbTop) * maxScroll;
      };

      const onMouseUp = () => {
        isDragging = false;
        thumb.style.cursor = 'grab';
        if (!alwaysSolid) {
          hideTimeout = setTimeout(() => {
            track.style.opacity = '0.35';
            thumb.style.opacity = '0.4';
          }, 1000);
        }
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      const showRestState = () => {
        syncPosition();
        const scrollable = scroller.scrollHeight > scroller.clientHeight;
        setScrollableInset(scrollable);
        if (!scrollable && !options.alwaysVisible) {
          track.style.opacity = '0';
          return;
        }
        applyThumbPosition();
        track.style.opacity = alwaysSolid ? '1' : '0.35';
        thumb.style.opacity = alwaysSolid ? '1' : '0.4';
      };

      thumb.addEventListener('mousedown', onThumbMouseDown);
      scroller.addEventListener('scroll', updateThumb);

      const resizeObserver = new ResizeObserver(() => {
        if (isDragging) {
          syncPosition();
          updateThumb();
        } else {
          showRestState();
        }
      });
      resizeObserver.observe(scroller);
      if (trackParent !== scroller) {
        resizeObserver.observe(trackParent);
      }
      const contentElement = options.getContentElement?.(scroller);
      if (contentElement) {
        resizeObserver.observe(contentElement);
      }

      // When no explicit content element is provided to observe, watch the scroller's
      // subtree so the scrollbar appears/updates as soon as content grows or shrinks
      // (e.g. items added or edited) — without needing a remount or page refresh.
      // Callers that pass getContentElement (e.g. the data grid) already observe their
      // own content element via ResizeObserver and skip this to avoid redundant work.
      let contentSyncRaf = 0;
      let contentMutationObserver: MutationObserver | undefined;
      if (!options.getContentElement) {
        contentMutationObserver = new MutationObserver(() => {
          if (isDragging) return;
          cancelAnimationFrame(contentSyncRaf);
          contentSyncRaf = requestAnimationFrame(showRestState);
        });
        contentMutationObserver.observe(scroller, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['style', 'class', 'hidden'],
        });
      }

      syncPosition();
      const t1 = setTimeout(showRestState, 200);
      const t2 = setTimeout(showRestState, 1000);

      teardown = () => {
        clearTimeout(hideTimeout);
        clearTimeout(t1);
        clearTimeout(t2);
        scroller.removeEventListener('scroll', updateThumb);
        thumb.removeEventListener('mousedown', onThumbMouseDown);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        resizeObserver.disconnect();
        contentMutationObserver?.disconnect();
        cancelAnimationFrame(contentSyncRaf);
        clipWrapper.remove();
        scroller.style.removeProperty(CALENDAR_SCROLL_INSET_CSS_VAR);
        if (originalPosition === 'static') {
          trackParent.style.position = '';
        }
      };

      return true;
    };

    if (!init()) {
      const waitObserver = new MutationObserver(() => {
        if (init()) {
          waitObserver.disconnect();
        }
      });
      waitObserver.observe(container, { childList: true, subtree: true });
      return () => {
        waitObserver.disconnect();
        teardown?.();
      };
    }

    return () => teardown?.();
    // options.getScroller/getTrackParent/getTrackBounds are intentionally excluded —
    // they are expected to be stable refs; options.key and options.enabled cover re-init cases.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    containerRef,
    options.enabled,
    options.key,
    theme.palette.scrollbar.thumb,
    theme.palette.scrollbar.track,
  ]);
};

export default useVerticalScrollbar;
